import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography, Button, Skeleton } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { HcpAlert, ALERT_SIZE, SEVERITY } from '@hmhco/hcp-alert';
import TenantPickerLayout from '../../Layout/TenantPickerLayout';
import Logo from '../../Logo/Logo';
import useStyles from './EarlyAccessCreateUserPage.styles';
import RequiredFieldsNote from '../../RequiredFieldsNote/RequiredFieldsNote';
import NameField from './NameField';
import UsernameField from './UsernameField';
import SchoolAssociationField from './SchoolAssociationField';
import PrimarySchoolField from './PrimarySchoolField';
import EmailField from './EmailField';
import { VALID_EMAIL_PATTERN } from '../EarlyAccessPage/EarlyAccessDomain';
import useDistrictSchools from '../../../../hooks/useDistrictSchools.hook';
import useSubmitEarlyAccessRegistration from '../../../../hooks/useSubmitEarlyAccessRegistration.hook';
import CreateUserButton from './CreateUserButton';
import nameFieldValidator from '../../../../util/nameFieldValidator';
import usernameValidator from '../../../../util/usernameValidator';
import { AUTO_COMPLETE } from '../../../../constants/constants';
import passwordValidator from '../../../../util/passwordValidator';
import formDataValidator from '../../../../util/formDataValidator';
import PasswordField from './PasswordField';
import {
  setEarlyAccessCreateUserFirstName,
  setEarlyAccessCreateUserLastName,
  setEarlyAccessCreateUserUsername,
  setEarlyAccessCreateUserPassword,
  setEarlyAccessCreateUserAssociatedSchools,
  setEarlyAccessCreateUserStep,
  setEarlyAccessCreateUserLoading,
  setEarlyAccessCreateUserSuccess,
  setEarlyAccessCreateUserError,
  setEarlyAccessCreateUserEmail,
  setEarlyAccessCreateUserDefaultSchool,
} from '../../../../store/actions/EarlyAccessCreateUserActions';
import {
  getEarlyAccessFirstName,
  getEarlyAccessLastName,
  getEarlyAccessUsername,
  getEarlyAccessPassword,
  getEarlyAccessAssociatedSchools,
  getEarlyAccessCreateUserStep,
  getEarlyAccessCreateUserIsErrored,
  getEarlyAccessCreateUserHasSucceeded,
  getEarlyAccessCreateUserEmail,
  getEarlyAccessDefaultSchool,
} from '../../../../store/selectors/EarlyAccessCreateUserState';

const errorMessageIds = {
  usernameExists: 'earlyAccess.createUser.username.exists',
  failedToCreateUser: 'earlyAccess.createUser.message.error.failed',
  schoolAssociationRequired:
    'earlyAccess.createUser.schoolAssociation.required',
  primarySchoolRequired: 'earlyAccess.createUser.primarySchool.required',
  emailInvalid: 'earlyAccess.createUser.email.invalid',
};

const EarlyAccessCreateUserPage = () => {
  const { classes } = useStyles();
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const firstName = useSelector(getEarlyAccessFirstName) ?? '';
  const lastName = useSelector(getEarlyAccessLastName) ?? '';
  const username = useSelector(getEarlyAccessUsername) ?? '';
  const password = useSelector(getEarlyAccessPassword);
  const schoolAssociation = useSelector(getEarlyAccessAssociatedSchools);
  const email = useSelector(getEarlyAccessCreateUserEmail) ?? '';
  const defaultSchool = useSelector(getEarlyAccessDefaultSchool);
  const {
    mutateAsync: submitRegistration,
  } = useSubmitEarlyAccessRegistration();
  const urlParams = new URLSearchParams(window.location.search);
  const urlOrgRefId = urlParams.get('org_ref_id');
  const connection = urlParams.get('connection');
  const signInHref = connection
    ? `/ui/login/?connection=${encodeURIComponent(connection)}`
    : '/ui/login/';
  const {
    data: districtSchools,
    isLoading: isLoadingSchools,
    isError: hasSchoolsError,
  } = useDistrictSchools(urlOrgRefId);
  const hasNoSchools =
    !urlOrgRefId ||
    hasSchoolsError ||
    (!isLoadingSchools && !districtSchools?.length);
  const step = useSelector(getEarlyAccessCreateUserStep);
  const isErrored = useSelector(getEarlyAccessCreateUserIsErrored);
  const hasSucceeded = useSelector(getEarlyAccessCreateUserHasSucceeded);

  const [verifyPassword, setVerifyPassword] = useState(undefined);
  const [touched, setTouched] = useState({});

  const firstNameLabel = formatMessage({
    id: 'earlyAccess.createUser.label.firstName',
  });
  const lastNameLabel = formatMessage({
    id: 'earlyAccess.createUser.label.lastName',
  });

  const onBack = () => {
    dispatch(setEarlyAccessCreateUserStep(1));
  };

  const getErrors = currentDetails => {
    const errors = {};

    const {
      isNameValid: isFirstNameValid,
      nameErrorMessageId: firstNameErrorMessageId,
    } = nameFieldValidator(currentDetails.firstName, 'firstName');

    const {
      isNameValid: isLastNameValid,
      nameErrorMessageId: lastNameErrorMessageId,
    } = nameFieldValidator(currentDetails.lastName, 'lastName');

    const { isUsernameValid, usernameErrorMessageId } = usernameValidator(
      currentDetails.username,
    );

    if (!isFirstNameValid) {
      errors.firstName = firstNameErrorMessageId;
    }

    if (!isLastNameValid) {
      errors.lastName = lastNameErrorMessageId;
    }

    if (!isUsernameValid) {
      errors.username = usernameErrorMessageId;
    }

    if (!currentDetails.schoolAssociation) {
      errors.schoolAssociation = errorMessageIds.schoolAssociationRequired;
    }

    if (!currentDetails.defaultSchool) {
      errors.defaultSchool = errorMessageIds.primarySchoolRequired;
    }

    if (!VALID_EMAIL_PATTERN[0].test(currentDetails.email)) {
      errors.email = errorMessageIds.emailInvalid;
    }

    return errors;
  };

  const handleChange = event => {
    event.persist();
    const { id, value } = event.target;

    if (id === 'firstName') dispatch(setEarlyAccessCreateUserFirstName(value));
    if (id === 'lastName') dispatch(setEarlyAccessCreateUserLastName(value));
    if (id === 'username') dispatch(setEarlyAccessCreateUserUsername(value));
    if (id === 'email') dispatch(setEarlyAccessCreateUserEmail(value));

    setTouched(currentTouched => ({ ...currentTouched, [id]: true }));
  };

  const handleBlur = event => {
    event.persist();
    const { id } = event.target;
    setTouched(currentTouched => ({ ...currentTouched, [id]: true }));
  };

  const handleSchoolChange = selectedSchool => {
    dispatch(setEarlyAccessCreateUserAssociatedSchools(selectedSchool));
    setTouched(currentTouched => ({
      ...currentTouched,
      schoolAssociation: true,
    }));
  };

  const handlePrimarySchoolChange = selectedSchool => {
    dispatch(setEarlyAccessCreateUserDefaultSchool(selectedSchool));
    setTouched(currentTouched => ({ ...currentTouched, defaultSchool: true }));
  };

  const { isPasswordValid, passwordErrorMessageId } = passwordValidator(
    password ?? undefined,
  );

  const { passwordsDoNotMatchErrorMessageId } = formDataValidator(
    password,
    verifyPassword,
  );

  const isStep1Complete =
    firstName !== '' &&
    lastName !== '' &&
    Boolean(schoolAssociation?.length) &&
    Boolean(defaultSchool);

  const handleNextStep = () => {
    dispatch(setEarlyAccessCreateUserStep(2));
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (step !== 2) return;
    dispatch(setEarlyAccessCreateUserLoading());
    try {
      await submitRegistration({
        id: urlParams.get('id'),
        orgRefId: urlOrgRefId,
        pid: urlParams.get('connection'),
        token: urlParams.get('token'),
        username,
        firstName,
        lastName,
        email,
        password,
        associatedSchools: schoolAssociation.map(school => school.id),
        defaultSchool: defaultSchool.id,
      });
      dispatch(setEarlyAccessCreateUserSuccess());
    } catch (error) {
      dispatch(setEarlyAccessCreateUserError());
    }
  };

  const errors = getErrors({
    firstName,
    lastName,
    username,
    schoolAssociation,
    defaultSchool,
    email,
  });

  const verifyPasswordErrorMessage = passwordsDoNotMatchErrorMessageId
    ? formatMessage({
        id: passwordsDoNotMatchErrorMessageId,
      })
    : undefined;
  const passwordLabel = formatMessage({
    id: 'tenantPicker.newPassword.newPasswordLabel',
  });
  const reEnterPasswordLabel = formatMessage({
    id: 'tenantPicker.newPassword.reEnterNewPasswordLabel',
  });
  const passwordErrorMessage = passwordErrorMessageId
    ? formatMessage({
        id: passwordErrorMessageId,
      })
    : undefined;

  const handlePasswordChange = event => {
    dispatch(setEarlyAccessCreateUserPassword(event.target.value));
  };
  const handleVerifyPasswordChange = event =>
    setVerifyPassword(event.target.value);

  return (
    <TenantPickerLayout>
      <header>
        <Logo />
        {!hasSucceeded && (
          <div className={classes.title}>
            <Typography component="h1" variant="h3">
              <FormattedMessage id="earlyAccess.createUser.title" />
            </Typography>
            <RequiredFieldsNote />
          </div>
        )}
      </header>
      <main>
        {hasNoSchools && (
          <HcpAlert
            size={ALERT_SIZE.SMALL}
            severity={SEVERITY.ERROR}
            subtitle={formatMessage({
              id: 'earlyAccess.createUser.message.error.schools',
            })}
          />
        )}
        {!hasNoSchools && isLoadingSchools && (
          <Skeleton variant="rectangular" height={200} />
        )}
        {!hasNoSchools && !isLoadingSchools && hasSucceeded ? (
          <>
            <HcpAlert
              size={ALERT_SIZE.SMALL}
              severity={SEVERITY.SUCCESS}
              title={formatMessage({
                id: 'earlyAccess.createUser.message.success.title',
              })}
              subtitle={formatMessage({
                id: 'earlyAccess.createUser.message.success.body',
              })}
            />
            <div className={classes.successButton}>
              <Button variant="outlined" color="primary" href={signInHref}>
                <FormattedMessage id="earlyAccess.createUser.button.signIn" />
              </Button>
            </div>
          </>
        ) : null}
        {!hasNoSchools && !isLoadingSchools && !hasSucceeded && (
          <>
            {isErrored && (
              <HcpAlert
                size={ALERT_SIZE.SMALL}
                severity={SEVERITY.ERROR}
                subtitle={formatMessage({
                  id: 'earlyAccess.createUser.message.error.failed',
                })}
              />
            )}
            <form className={classes.form} onSubmit={handleSubmit}>
              {step === 1 && (
                <>
                  <SchoolAssociationField
                    schools={districtSchools}
                    selectedSchools={schoolAssociation}
                    errorMessageId={
                      touched.schoolAssociation && errors.schoolAssociation
                    }
                    handleChange={handleSchoolChange}
                  />
                  <PrimarySchoolField
                    associatedSchools={schoolAssociation}
                    selectedSchool={defaultSchool}
                    errorMessageId={
                      touched.defaultSchool && errors.defaultSchool
                    }
                    handleChange={handlePrimarySchoolChange}
                  />
                  <NameField
                    name={firstName}
                    nameLabel={firstNameLabel}
                    errorMessageId={touched.firstName && errors.firstName}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    elementId="firstName"
                  />
                  <NameField
                    name={lastName}
                    nameLabel={lastNameLabel}
                    errorMessageId={touched.lastName && errors.lastName}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    elementId="lastName"
                    autoComplete={AUTO_COMPLETE.FAMILY_NAME}
                  />
                  <CreateUserButton
                    isDisabled={!isStep1Complete}
                    onClick={handleNextStep}
                    type="button"
                    labelId="earlyAccess.createUser.button.next"
                  />
                </>
              )}
              {step === 2 && (
                <>
                  <UsernameField
                    username={username}
                    errorMessageId={touched.username && errors.username}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  />
                  <Typography variant="body2">
                    <FormattedMessage id="earlyAccess.usernameRequirements" />
                  </Typography>
                  <EmailField
                    email={email}
                    errorMessageId={touched.email && errors.email}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  />
                  <PasswordField
                    dataTestId="new-password-password"
                    handlePasswordChange={handlePasswordChange}
                    errorMessage={passwordErrorMessage}
                    passwordLabel={passwordLabel}
                    elementId="new-password-enter-password"
                  />
                  <PasswordField
                    dataTestId="new-password-validate-password"
                    handlePasswordChange={handleVerifyPasswordChange}
                    errorMessage={verifyPasswordErrorMessage}
                    passwordLabel={reEnterPasswordLabel}
                    elementId="new-password-reenter-password"
                  />
                  <CreateUserButton
                    onBack={onBack}
                    isDisabled={
                      !isPasswordValid ||
                      Boolean(errors.email) ||
                      Boolean(passwordsDoNotMatchErrorMessageId) ||
                      !verifyPassword
                    }
                  />
                </>
              )}
            </form>
          </>
        )}
      </main>
    </TenantPickerLayout>
  );
};

export default EarlyAccessCreateUserPage;
